import React, { useState, useMemo } from 'react';
import { 
  FiUsers, FiActivity, FiClock, FiCheck, FiAlertCircle, FiZap,
  FiPackage, FiSearch, FiChevronRight, FiTrendingUp,
  FiTrendingDown, FiX, FiBarChart2, FiRefreshCw, FiAward, FiTarget
} from 'react-icons/fi';
import OrderStory from './OrderStory';

// Calculate urgency based on inStockDate
function isOrderUrgent(order) {
  if (!order?.inStockDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const orderDate = new Date(order.inStockDate);
  orderDate.setHours(0, 0, 0, 0);
  const dayDiff = Math.ceil((orderDate - today) / (1000 * 60 * 60 * 24));
  return dayDiff === 1;
}

// Effort badge colors with solid backgrounds and clear edges
const EFFORT_COLORS = {
  1: 'bg-[#059669] text-white ring-1 ring-[#047857]',
  2: 'bg-[#0284C7] text-white ring-1 ring-[#0369A1]',
  3: 'bg-[#D97706] text-white ring-1 ring-[#B45309]',
  4: 'bg-[#EA580C] text-white ring-1 ring-[#C2410C]',
  5: 'bg-[#DC2626] text-white ring-1 ring-[#B91C1C]',
};

// Priority styles with clear edge definition
const PRIORITY_BADGE = {
  P0: 'bg-[#B91C1C] text-white ring-1 ring-[#7F1D1D] shadow-[0_2px_4px_rgba(0,0,0,0.1)]',
  P1: 'bg-[#D97706] text-white ring-1 ring-[#92400E]/50 shadow-[0_2px_4px_rgba(0,0,0,0.1)]',
  P2: 'bg-[#E4E4E7] text-[#3F3F46] ring-1 ring-[#D4D4D8] dark:bg-[#3F3F46] dark:text-[#E4E4E7] dark:ring-[#52525B]',
};

export default function TicketAdminDashboard({
  tickets = [],
  orders = [],
  dispatchers = [],
  onReassignTicket,
  onEscalateTicket,
  onRefresh,
}) {
  const [selectedDispatcher, setSelectedDispatcher] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewMode, setViewMode] = useState('dispatchers');
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [showTicketDetail, setShowTicketDetail] = useState(null);

  // Create lookup maps
  const orderMap = useMemo(() => {
    const map = {};
    orders.forEach(o => { map[o._id] = o; });
    return map;
  }, [orders]);

  const dispatcherMap = useMemo(() => {
    const map = {};
    dispatchers.forEach(d => { map[d._id] = d; });
    return map;
  }, [dispatchers]);

  // Enrich tickets
  const enrichedTickets = useMemo(() => {
    return tickets.map(ticket => {
      const order = orderMap[ticket.orderId];
      const dispatcher = dispatcherMap[ticket.assignedTo];
      return {
        ...ticket,
        order,
        dispatcher,
        isUrgent: order ? isOrderUrgent(order) : false,
        isOverdue: ticket.dueAt ? new Date(ticket.dueAt) < new Date() : false,
      };
    });
  }, [tickets, orderMap, dispatcherMap]);

  // Use real metrics data if available, fallback to calculated stats
  const dispatcherStats = useMemo(() => {
    // If we have real metrics data from backend, use it
    if (dispatchers && dispatchers.length > 0 && dispatchers[0].avgCompletionTime !== undefined) {
      return dispatchers.map(d => ({
        dispatcher: d,
        total: d.totalTickets || 0,
        completed: d.completedTickets || 0,
        inProgress: d.inProgress || 0,
        pending: d.pending || 0,
        urgent: d.urgent || 0,
        overdue: d.overdue || 0,
        avgCompletionTime: d.avgCompletionTime || 0,
        totalEffort: d.totalEffort || 0,
        tickets: d.tickets || [],
      })).sort((a, b) => b.total - a.total);
    }

    // Fallback to calculated stats
    const stats = {};

    dispatchers.forEach(d => {
      stats[d._id] = {
        dispatcher: d,
        total: 0,
        completed: 0,
        inProgress: 0,
        pending: 0,
        urgent: 0,
        overdue: 0,
        avgCompletionTime: 0,
        totalEffort: 0,
        tickets: [],
      };
    });

    enrichedTickets.forEach(ticket => {
      const id = ticket.assignedTo;
      if (stats[id]) {
        stats[id].total++;
        stats[id].tickets.push(ticket);
        stats[id].totalEffort += ticket.effort || 3;

        if (ticket.status === 'completed') stats[id].completed++;
        else if (ticket.status === 'in_progress') stats[id].inProgress++;
        else stats[id].pending++;

        if (ticket.isUrgent) stats[id].urgent++;
        if (ticket.isOverdue) stats[id].overdue++;
      }
    });

    Object.values(stats).forEach(s => {
      const completedTickets = s.tickets.filter(t => t.status === 'completed' && t.durationMinutes);
      if (completedTickets.length > 0) {
        s.avgCompletionTime = Math.round(
          completedTickets.reduce((sum, t) => sum + t.durationMinutes, 0) / completedTickets.length
        );
      }
    });

    return Object.values(stats).sort((a, b) => b.total - a.total);
  }, [dispatchers, enrichedTickets]);

  // Order stories grouped by orderId
  const orderStories = useMemo(() => {
    const stories = {};
    enrichedTickets.forEach(ticket => {
      const orderId = ticket.orderId;
      if (!stories[orderId]) {
        stories[orderId] = {
          order: ticket.order,
          tickets: [],
          totalEffort: 0,
          hasUrgent: false,
          hasOverdue: false,
          completionPercent: 0,
        };
      }
      stories[orderId].tickets.push(ticket);
      stories[orderId].totalEffort += ticket.effort || 3;
      if (ticket.isUrgent) stories[orderId].hasUrgent = true;
      if (ticket.isOverdue) stories[orderId].hasOverdue = true;
    });

    Object.values(stories).forEach(story => {
      const completed = story.tickets.filter(t => t.status === 'completed').length;
      story.completionPercent = Math.round((completed / story.tickets.length) * 100);
    });

    return Object.values(stories).sort((a, b) => {
      if (a.hasOverdue && !b.hasOverdue) return -1;
      if (!a.hasOverdue && b.hasOverdue) return 1;
      if (a.hasUrgent && !b.hasUrgent) return -1;
      if (!a.hasUrgent && b.hasUrgent) return 1;
      return a.completionPercent - b.completionPercent;
    });
  }, [enrichedTickets]);

  // Overall stats
  const overallStats = useMemo(() => ({
    totalTickets: enrichedTickets.length,
    completed: enrichedTickets.filter(t => t.status === 'completed').length,
    inProgress: enrichedTickets.filter(t => t.status === 'in_progress').length,
    pending: enrichedTickets.filter(t => t.status === 'pending').length,
    urgent: enrichedTickets.filter(t => t.isUrgent).length,
    overdue: enrichedTickets.filter(t => t.isOverdue).length,
    activeDispatchers: dispatchers.filter(d => 
      enrichedTickets.some(t => t.assignedTo === d._id && t.status === 'in_progress')
    ).length,
  }), [enrichedTickets, dispatchers]);

  // Filter tickets
  const filteredTickets = useMemo(() => {
    let filtered = enrichedTickets;
    
    if (selectedDispatcher) {
      filtered = filtered.filter(t => t.assignedTo === selectedDispatcher._id);
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter(t => t.priority === priorityFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.name?.toLowerCase().includes(q) ||
        t.orderUniqueId?.toLowerCase().includes(q) ||
        t.type?.toLowerCase().includes(q) ||
        t.dispatcher?.firstName?.toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [enrichedTickets, selectedDispatcher, priorityFilter, searchQuery]);

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <FiActivity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Command Center</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Real-time ticket monitoring</p>
            </div>
          </div>

          <button
            onClick={onRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <FiRefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Live Stats */}
        <div className="grid grid-cols-7 gap-4">
          <LiveStat label="Total" value={overallStats.totalTickets} icon={FiPackage} />
          <LiveStat label="Active" value={overallStats.inProgress} icon={FiActivity} color="sky" pulse />
          <LiveStat label="Pending" value={overallStats.pending} icon={FiClock} color="gray" />
          <LiveStat label="Done" value={overallStats.completed} icon={FiCheck} color="emerald" />
          <LiveStat label="Urgent" value={overallStats.urgent} icon={FiZap} color="amber" pulse={overallStats.urgent > 0} />
          <LiveStat label="Overdue" value={overallStats.overdue} icon={FiAlertCircle} color="red" pulse={overallStats.overdue > 0} />
          <LiveStat label="Staff" value={overallStats.activeDispatchers} icon={FiUsers} color="violet" />
        </div>
      </header>

      {/* View Toggle */}
      <div className="px-6 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ViewToggle active={viewMode === 'dispatchers'} onClick={() => setViewMode('dispatchers')} icon={FiUsers} label="Dispatchers" />
          <ViewToggle active={viewMode === 'orders'} onClick={() => setViewMode('orders')} icon={FiPackage} label="Order Stories" />
          <ViewToggle active={viewMode === 'metrics'} onClick={() => setViewMode('metrics')} icon={FiBarChart2} label="Metrics" />
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-700 border-0 rounded-xl text-sm focus:ring-2 focus:ring-violet-500 dark:text-white w-64"
            />
          </div>

          <select
            value={priorityFilter}
            onChange={e => setPriorityFilter(e.target.value)}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 border-0 rounded-xl text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-violet-500"
          >
            <option value="all">All Priorities</option>
            <option value="P0">P0 - Critical</option>
            <option value="P1">P1 - High</option>
            <option value="P2">P2 - Normal</option>
          </select>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {viewMode === 'dispatchers' && (
          <DispatcherView
            dispatcherStats={dispatcherStats}
            selectedDispatcher={selectedDispatcher}
            setSelectedDispatcher={setSelectedDispatcher}
            filteredTickets={filteredTickets}
            setShowTicketDetail={setShowTicketDetail}
            orderMap={orderMap}
            setSelectedOrder={setSelectedOrder}
          />
        )}

        {viewMode === 'orders' && (
          <OrdersView
            orderStories={orderStories}
            selectedOrder={selectedOrder}
            setSelectedOrder={setSelectedOrder}
            enrichedTickets={enrichedTickets}
          />
        )}

        {viewMode === 'metrics' && (
          <div className="flex-1 overflow-y-auto p-6">
            <MetricsGrid stats={overallStats} dispatcherStats={dispatcherStats} />
          </div>
        )}
      </div>

      {/* Ticket Detail Modal */}
      {showTicketDetail && (
        <TicketDetailModal
          ticket={showTicketDetail}
          order={orderMap[showTicketDetail.orderId]}
          dispatcher={dispatcherMap[showTicketDetail.assignedTo]}
          onClose={() => setShowTicketDetail(null)}
          onReassign={onReassignTicket}
          onEscalate={onEscalateTicket}
        />
      )}
    </div>
  );
}

// Sub-components

function LiveStat({ label, value, icon: Icon, color = 'gray', pulse = false }) {
  const colors = {
    gray: 'bg-[#F4F4F5] text-[#52525B] ring-1 ring-[#E4E4E7] dark:bg-[#3F3F46] dark:text-[#D4D4D8] dark:ring-[#52525B]',
    sky: 'bg-[#DBEAFE] text-[#1E40AF] ring-1 ring-[#93C5FD]/50 dark:bg-[#1E3A5F] dark:text-[#93C5FD] dark:ring-[#3B82F6]/30',
    emerald: 'bg-[#D1FAE5] text-[#065F46] ring-1 ring-[#6EE7B7]/50 dark:bg-[#064E3B] dark:text-[#6EE7B7] dark:ring-[#10B981]/30',
    amber: 'bg-[#FEF3C7] text-[#92400E] ring-1 ring-[#FCD34D]/50 dark:bg-[#78350F] dark:text-[#FCD34D] dark:ring-[#F59E0B]/30',
    red: 'bg-[#FEE2E2] text-[#991B1B] ring-1 ring-[#FCA5A5]/50 dark:bg-[#7F1D1D] dark:text-[#FCA5A5] dark:ring-[#EF4444]/30',
    violet: 'bg-[#EDE9FE] text-[#5B21B6] ring-1 ring-[#C4B5FD]/50 dark:bg-[#4C1D95] dark:text-[#C4B5FD] dark:ring-[#8B5CF6]/30',
  };

  return (
    <div className={`rounded-xl p-3 ${colors[color]} ${pulse ? 'animate-pulse' : ''}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-medium opacity-75">{label}</span>
      </div>
      <span className="text-2xl font-bold">{value}</span>
    </div>
  );
}

function ViewToggle({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
        active 
          ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' 
          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

function DispatcherAvatar({ dispatcher, size = 'md', showStatus = false }) {
  const sizes = { sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm', lg: 'w-14 h-14 text-lg' };
  const initials = dispatcher 
    ? `${dispatcher.firstName?.[0] || ''}${dispatcher.lastName?.[0] || ''}`
    : '?';
  const colors = ['bg-violet-500', 'bg-sky-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500'];
  const colorIndex = dispatcher?.firstName?.charCodeAt(0) % colors.length || 0;

  return (
    <div className={`relative ${sizes[size]} rounded-full ${colors[colorIndex]} text-white font-bold flex items-center justify-center`}>
      {dispatcher?.profilePic ? (
        <img src={dispatcher.profilePic} alt="" className="w-full h-full rounded-full object-cover" />
      ) : (
        initials
      )}
      {showStatus && (
        <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white dark:border-gray-800" />
      )}
    </div>
  );
}

function DispatcherView({ dispatcherStats, selectedDispatcher, setSelectedDispatcher, filteredTickets, setShowTicketDetail, orderMap, setSelectedOrder }) {
  return (
    <div className="flex-1 flex">
      <div className="w-80 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-y-auto">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="font-semibold text-gray-900 dark:text-white">Team ({dispatcherStats.length})</h2>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {dispatcherStats.map(stats => (
            <DispatcherCard
              key={stats.dispatcher._id}
              stats={stats}
              isSelected={selectedDispatcher?._id === stats.dispatcher._id}
              onClick={() => setSelectedDispatcher(selectedDispatcher?._id === stats.dispatcher._id ? null : stats.dispatcher)}
            />
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {selectedDispatcher ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <DispatcherAvatar dispatcher={selectedDispatcher} size="lg" />
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                    {selectedDispatcher.firstName} {selectedDispatcher.lastName}
                  </h2>
                  <p className="text-sm text-gray-500">{filteredTickets.length} tickets</p>
                </div>
              </div>
              <button onClick={() => setSelectedDispatcher(null)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <FiX className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <TicketList tickets={filteredTickets} onViewTicket={setShowTicketDetail} onViewOrder={(id) => setSelectedOrder(orderMap[id])} />
          </>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            {dispatcherStats.map(stats => (
              <DispatcherMiniCard key={stats.dispatcher._id} stats={stats} onClick={() => setSelectedDispatcher(stats.dispatcher)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DispatcherCard({ stats, isSelected, onClick }) {
  const { dispatcher, total, completed, inProgress, urgent, overdue } = stats;

  return (
    <button
      onClick={onClick}
      className={`w-full p-4 text-left transition-all hover:bg-gray-50 dark:hover:bg-gray-700 ${
        isSelected ? 'bg-violet-50 dark:bg-violet-900/20 border-l-4 border-violet-500' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        <DispatcherAvatar dispatcher={dispatcher} showStatus={inProgress > 0} />
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 dark:text-white truncate">
            {dispatcher.firstName} {dispatcher.lastName}
          </h3>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>{total} tickets</span>
            {urgent > 0 && <span className="text-amber-600 flex items-center gap-0.5"><FiZap className="w-3 h-3" />{urgent}</span>}
            {overdue > 0 && <span className="text-red-600 flex items-center gap-0.5"><FiAlertCircle className="w-3 h-3" />{overdue}</span>}
          </div>
        </div>
        <div className="text-right">
          <span className="text-xs font-medium text-gray-600">{completed}/{total}</span>
          <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full mt-1 overflow-hidden">
            <div className="h-full bg-emerald-500" style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }} />
          </div>
        </div>
      </div>
    </button>
  );
}

function DispatcherMiniCard({ stats, onClick }) {
  const { dispatcher, total, completed, inProgress, urgent, overdue, totalEffort } = stats;

  return (
    <button
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-2xl p-5 text-left shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:border-violet-300 transition-all group"
    >
      <div className="flex items-start justify-between mb-4">
        <DispatcherAvatar dispatcher={dispatcher} size="lg" showStatus={inProgress > 0} />
        <FiChevronRight className="w-5 h-5 text-gray-400 group-hover:text-violet-500" />
      </div>
      <h3 className="font-bold text-gray-900 dark:text-white mb-1">{dispatcher.firstName} {dispatcher.lastName}</h3>
      <div className="flex items-center gap-3 text-sm mb-4 text-gray-500">{total} tickets • {totalEffort} pts</div>
      <div className="flex items-center gap-2">
        {inProgress > 0 && <span className="px-2 py-1 rounded-lg bg-[#DBEAFE] text-[#1E40AF] text-xs font-medium ring-1 ring-[#93C5FD]/50">{inProgress} active</span>}
        {urgent > 0 && <span className="px-2 py-1 rounded-lg bg-[#FEF3C7] text-[#92400E] text-xs font-medium ring-1 ring-[#FCD34D]/50 animate-pulse">{urgent} urgent</span>}
        {overdue > 0 && <span className="px-2 py-1 rounded-lg bg-[#FEE2E2] text-[#991B1B] text-xs font-medium ring-1 ring-[#FCA5A5]/50 animate-pulse">{overdue} overdue</span>}
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>Progress</span>
          <span>{total > 0 ? Math.round((completed / total) * 100) : 0}%</span>
        </div>
        <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-violet-500 to-purple-500" style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }} />
        </div>
      </div>
    </button>
  );
}

function OrdersView({ orderStories, selectedOrder, setSelectedOrder, enrichedTickets }) {
  return (
    <div className="flex-1 flex">
      <div className="w-96 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-y-auto">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="font-semibold text-gray-900 dark:text-white">Order Stories ({orderStories.length})</h2>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {orderStories.map(story => (
            <OrderStoryCard
              key={story.order?._id || Math.random()}
              story={story}
              isSelected={selectedOrder?._id === story.order?._id}
              onClick={() => setSelectedOrder(story.order)}
            />
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-6">
        {selectedOrder ? (
          <OrderStory
            order={selectedOrder}
            tickets={enrichedTickets.filter(t => t.orderId === selectedOrder._id)}
            onClose={() => setSelectedOrder(null)}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <FiPackage className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>Select an order to view its story</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OrderStoryCard({ story, isSelected, onClick }) {
  const { order, tickets, completionPercent, hasUrgent, hasOverdue, totalEffort } = story;

  return (
    <button
      onClick={onClick}
      className={`w-full p-4 text-left transition-all hover:bg-gray-50 dark:hover:bg-gray-700 ${
        isSelected ? 'bg-violet-50 dark:bg-violet-900/20 border-l-4 border-violet-500' : ''
      } ${hasOverdue ? 'bg-red-50 dark:bg-red-900/10' : hasUrgent ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="font-medium text-gray-900 dark:text-white">Order #{order?.uniqueId || 'Unknown'}</h3>
          <p className="text-xs text-gray-500">{order?.property?.address?.city || 'Unknown location'}</p>
        </div>
        <div className="flex items-center gap-1">
          {hasOverdue && <span className="w-2 h-2 rounded-full bg-[#EF4444] ring-1 ring-[#B91C1C] animate-pulse" />}
          {hasUrgent && !hasOverdue && <span className="w-2 h-2 rounded-full bg-[#F59E0B] ring-1 ring-[#B45309] animate-pulse" />}
        </div>
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
        <span>{tickets.length} tickets</span>
        <span className="text-gray-300">•</span>
        <span>{totalEffort} pts</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
          <div className={`h-full ${completionPercent === 100 ? 'bg-emerald-500' : 'bg-violet-500'}`} style={{ width: `${completionPercent}%` }} />
        </div>
        <span className="text-xs font-medium text-gray-600">{completionPercent}%</span>
      </div>
    </button>
  );
}

function TicketList({ tickets, onViewTicket, onViewOrder }) {
  if (tickets.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <FiPackage className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>No tickets found</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {tickets.map(ticket => (
        <div
          key={ticket.id}
          onClick={() => onViewTicket(ticket)}
          className={`p-4 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
            ticket.isOverdue 
              ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 ticket-overdue-shake' 
              : ticket.isUrgent 
                ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 ticket-urgent-pulse' 
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold shadow-[0_2px_4px_rgba(0,0,0,0.1)] ${EFFORT_COLORS[ticket.effort || 3]}`}>
                {ticket.effort || 3}
              </span>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white text-sm">{ticket.name || ticket.type}</h4>
                <button onClick={(e) => { e.stopPropagation(); onViewOrder(ticket.orderId); }} className="text-xs text-sky-600 hover:underline">
                  Order #{ticket.orderUniqueId}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${PRIORITY_BADGE[ticket.priority] || PRIORITY_BADGE.P2}`}>
                {ticket.priority || 'P2'}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                ticket.status === 'completed' ? 'bg-[#D1FAE5] text-[#065F46] ring-1 ring-[#6EE7B7]/50' :
                ticket.status === 'in_progress' ? 'bg-[#DBEAFE] text-[#1E40AF] ring-1 ring-[#93C5FD]/50' :
                'bg-[#F4F4F5] text-[#52525B] ring-1 ring-[#E4E4E7]'
              }`}>
                {ticket.status || 'pending'}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TicketDetailModal({ ticket, order, dispatcher, onClose, onReassign, onEscalate }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Ticket Details</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <FiX className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">
          <div className="grid grid-cols-2 gap-6">
            <DetailRow label="Name" value={ticket.name || ticket.type} />
            <DetailRow label="Type" value={ticket.type} />
            <DetailRow label="Priority" value={ticket.priority} />
            <DetailRow label="Effort" value={`${ticket.effort || 3} points`} />
            <DetailRow label="Status" value={ticket.status} />
            <DetailRow label="Order" value={`#${ticket.orderUniqueId}`} />
            <DetailRow label="Assigned To" value={dispatcher ? `${dispatcher.firstName} ${dispatcher.lastName}` : 'Unassigned'} />
            <DetailRow label="Created" value={ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : '—'} />
          </div>
          {order && (
            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Order Context</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <DetailRow label="Guest" value={order.guestEmail?.split('@')[0] || 'N/A'} />
                <DetailRow label="Date" value={order.inStockDate ? new Date(order.inStockDate).toLocaleDateString() : '—'} />
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
          <button onClick={() => onReassign?.(ticket)} className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100">Reassign</button>
          <button onClick={() => onEscalate?.(ticket)} className="px-4 py-2 rounded-xl bg-amber-100 text-amber-700 text-sm font-medium hover:bg-amber-200">Escalate</button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="font-medium text-gray-900 dark:text-white">{value || '—'}</dd>
    </div>
  );
}

function MetricsGrid({ stats, dispatcherStats }) {
  const avgTicketsPerPerson = dispatcherStats.length > 0 ? Math.round(stats.totalTickets / dispatcherStats.length) : 0;
  const avgCompletionTime = dispatcherStats.length > 0
    ? Math.round(dispatcherStats.reduce((sum, s) => sum + s.avgCompletionTime, 0) / dispatcherStats.length)
    : 0;
  const topPerformers = [...dispatcherStats].sort((a, b) => b.completed - a.completed).slice(0, 3);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-4">
        <MetricCard title="Completion Rate" value={`${stats.totalTickets > 0 ? Math.round((stats.completed / stats.totalTickets) * 100) : 0}%`} subtitle={`${stats.completed}/${stats.totalTickets}`} icon={FiTarget} color="violet" />
        <MetricCard title="Avg Time" value={`${avgCompletionTime}m`} subtitle="per ticket" icon={FiClock} color="sky" />
        <MetricCard title="Per Person" value={avgTicketsPerPerson} subtitle="avg load" icon={FiUsers} color="emerald" />
        <MetricCard title="Overdue" value={stats.overdue} subtitle="breaches" icon={FiAlertCircle} color="red" />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FiAward className="w-5 h-5 text-amber-500" />
          Top Performers
        </h3>
        <div className="space-y-3">
          {topPerformers.map((s, idx) => (
            <div key={s.dispatcher._id} className="flex items-center gap-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                idx === 0 ? 'bg-amber-100 text-amber-700' : idx === 1 ? 'bg-gray-200 text-gray-700' : 'bg-orange-100 text-orange-700'
              }`}>{idx + 1}</span>
              <DispatcherAvatar dispatcher={s.dispatcher} />
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-white">{s.dispatcher.firstName} {s.dispatcher.lastName}</p>
                <p className="text-xs text-gray-500">{s.completed} completed • {s.totalEffort} pts</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-emerald-600">{s.completed}</p>
                <p className="text-xs text-gray-500">tickets</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FiBarChart2 className="w-5 h-5 text-violet-500" />
          Workload
        </h3>
        <div className="space-y-3">
          {dispatcherStats.map(s => (
            <div key={s.dispatcher._id} className="flex items-center gap-3">
              <DispatcherAvatar dispatcher={s.dispatcher} size="sm" />
              <span className="w-24 text-sm text-gray-700 dark:text-gray-300 truncate">{s.dispatcher.firstName}</span>
              <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex">
                <div className="bg-emerald-500 h-full" style={{ width: `${(s.completed / (s.total || 1)) * 100}%` }} />
                <div className="bg-sky-500 h-full" style={{ width: `${(s.inProgress / (s.total || 1)) * 100}%` }} />
                <div className="bg-gray-300 h-full" style={{ width: `${(s.pending / (s.total || 1)) * 100}%` }} />
              </div>
              <span className="w-12 text-right text-sm font-medium text-gray-600">{s.total}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-4 mt-4 text-xs text-gray-500">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500" /> Done</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-sky-500" /> Active</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-300" /> Pending</span>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, subtitle, icon: Icon, color }) {
  const colors = {
    violet: 'from-violet-500 to-purple-600',
    sky: 'from-sky-500 to-blue-600',
    emerald: 'from-emerald-500 to-green-600',
    red: 'from-red-500 to-rose-600',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className="text-xs text-gray-500 mt-1">{title}</p>
      {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
    </div>
  );
}

